'use client'

import { useState } from 'react'
import PropTypes from 'prop-types'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUILink from '@mui/material/Link'
import MUIPaper from '@mui/material/Paper'
import MUIRadio from '@mui/material/Radio'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import { useRadioGroup } from '@mui/material/RadioGroup'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useFormsStore } from '@repo/store/useFormsStore'
import { FormViewer } from '../FormViewer/FormViewer'

const VARIANTS = ['display', 'checkbox', 'radio']

const cardSx = {
  p: '16px 24px 16px 8px',
  border: '1px solid',
  borderColor: 'background.slate100',
  borderRadius: 1,
  boxShadow: 'none',
  bgcolor: 'common.white',
  overflow: 'hidden'
}

const selectedCardSx = {
  borderColor: 'secondary.main',
  bgcolor: 'secondary.focus'
}

const formControlLabelSx = {
  m: 0,
  width: '100%',
  alignItems: 'center',
  '& .MuiFormControlLabel-label': {
    flex: 1,
    minWidth: 0,
    ml: 1
  }
}

const RadioFormCardPaper = ({ value, body }) => {
  const radioGroup = useRadioGroup()
  const isSelected = radioGroup ? radioGroup.value === value : false

  return (
    <MUIPaper sx={{ ...cardSx, ...(isSelected ? selectedCardSx : {}) }}>
      <MUIFormControlLabel value={value} control={<MUIRadio />} label={body} sx={formControlLabelSx} />
    </MUIPaper>
  )
}

RadioFormCardPaper.propTypes = {
  value: PropTypes.string,
  body: PropTypes.node.isRequired
}

export const FormCard = ({
  variant = 'display',
  formId,
  name,
  description,
  formCategoryId,
  value,
  checked,
  onChange
}) => {
  const { formCategories, getFormPreviewUrls } = useFormsStore()

  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewUrls, setPreviewUrls] = useState([])

  const categoryName = formCategoryId ? formCategories?.find((c) => c?.formCategoryId === formCategoryId)?.name : null

  const handlePreviewClick = async (event) => {
    event.preventDefault()
    event.stopPropagation()

    const urls = await getFormPreviewUrls({ formId })
    setPreviewUrls(urls ?? [])
    setPreviewOpen(true)
  }

  const body = (
    <MUIStack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
      <MUIStack spacing={1} sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
        <MUITypography variant="h6" sx={{ cursor: 'pointer' }}>{name}</MUITypography>
        <MUITypography variant="body1" sx={{ pb: 0.5, cursor: 'pointer' }}>
          {description}
        </MUITypography>
        {categoryName && (
          <MUITypography
            variant="body2"
            sx={{
              bgcolor: 'background.slate100',
              borderRadius: '16px',
              px: 2,
              py: 0.75,
              display: 'inline-block',
              width: 'fit-content',
              whiteSpace: 'nowrap',
              cursor: 'pointer'
            }}
          >
            {categoryName}
          </MUITypography>
        )}
      </MUIStack>
      <MUILink
        component="button"
        type="button"
        onClick={handlePreviewClick}
        underline="always"
        color="secondary"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          background: 'none',
          border: 0,
          p: 0,
          cursor: 'pointer'
        }}
      >
        Preview form
        <OpenInNewIcon sx={{ fontSize: '1rem' }} />
      </MUILink>
    </MUIStack>
  )

  let card
  if (variant === 'checkbox') {
    card = (
      <MUIPaper sx={{ ...cardSx, ...(checked ? selectedCardSx : {}) }}>
        <MUIFormControlLabel
          control={<MUICheckbox checked={!!checked} onChange={onChange} value={value} />}
          label={body}
          sx={formControlLabelSx}
        />
      </MUIPaper>
    )
  } else if (variant === 'radio') {
    card = <RadioFormCardPaper value={value} body={body} />
  } else {
    card = <MUIPaper sx={{ ...cardSx, p: '16px 24px 16px 24px' }}>{body}</MUIPaper>
  }

  return (
    <>
      {card}
      <FormViewer open={previewOpen} onClose={() => setPreviewOpen(false)} formName={name} previewUrls={previewUrls} />
    </>
  )
}

FormCard.propTypes = {
  variant: PropTypes.oneOf(VARIANTS),
  formId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  formCategoryId: PropTypes.string,
  value: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func
}
